import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Phone,
  Mail,
  Clock,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import shifaLogo from "@/assets/images/shifa-logo.png";
import { siteConfig } from "@/data/site";
import { departments } from "@/data/departments";

const navLinks: { label: string; href: string; hasDropdown?: boolean }[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Departments", href: "/departments", hasDropdown: true },
  { label: "Doctors", href: "/doctors" },
  { label: "Exceptional Cases", href: "/rare-cases" },
  { label: "Contact", href: "/contact" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [mobileDeptOpen, setMobileDeptOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const deptRef = useRef<HTMLDivElement>(null);
  const deptTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setDeptOpen(false);
    setMobileDeptOpen(false);
  }, [location.pathname]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (deptRef.current && !deptRef.current.contains(e.target as Node)) {
        setDeptOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleDeptEnter = useCallback(() => {
    clearTimeout(deptTimeoutRef.current);
    setDeptOpen(true);
  }, []);

  const handleDeptLeave = useCallback(() => {
    deptTimeoutRef.current = setTimeout(() => setDeptOpen(false), 200);
  }, []);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Top info bar */}
      <div className="hidden bg-primary-900 text-white lg:block">
        <div className="container-app flex items-center justify-between py-1.5 text-xs">
          <div className="flex items-center gap-6">
            <a
              href={`tel:${siteConfig.contact.phone.replace(/-/g, "")}`}
              className="flex items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <Phone className="h-3 w-3" />
              <span>{siteConfig.contact.phone}</span>
            </a>
            <a
              href={`mailto:${siteConfig.contact.email}`}
              className="flex items-center gap-1.5 transition-opacity hover:opacity-80"
            >
              <Mail className="h-3 w-3" />
              <span>{siteConfig.contact.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            <span>Mon - Sat: Consultation | Sun: Emergency Only</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={cn(
          "border-b border-gray-100 bg-white/80 backdrop-blur-lg transition-shadow duration-300",
          scrolled && "shadow-sm shadow-gray-200/60",
        )}
      >
        <div className="container-app flex h-16 items-center justify-between lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={shifaLogo} alt="Shifa Medical Centre" className="h-12 w-auto" />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.label}
                  ref={deptRef}
                  className="relative"
                  onMouseEnter={handleDeptEnter}
                  onMouseLeave={handleDeptLeave}
                >
                  <button
                    onClick={() => setDeptOpen((p) => !p)}
                    className={cn(
                      "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive(link.href)
                        ? "text-primary-600"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                    )}
                  >
                    {link.label}
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 transition-transform duration-200",
                        deptOpen && "rotate-180",
                      )}
                    />
                  </button>

                  {/* Mega menu */}
                  <AnimatePresence>
                    {deptOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                        className="absolute left-1/2 top-full pt-2 -translate-x-1/2"
                      >
                        <div className="w-[640px] rounded-xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-200/50">
                          <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">
                              Our Departments
                            </h3>
                            <Link
                              to="/departments"
                              className="text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
                            >
                              View all &rarr;
                            </Link>
                          </div>
                          <div className="grid grid-cols-3 gap-1">
                            {departments.map((dept) => (
                              <Link
                                key={dept.id}
                                to={`/departments/${dept.id}`}
                                className="rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                              >
                                {dept.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={cn(
                    "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive(link.href)
                      ? "text-primary-600"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  )}
                >
                  {link.label}
                </Link>
              ),
            )}
          </div>

          {/* Desktop CTA buttons */}
          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={`tel:${siteConfig.contact.emergency.replace(/-/g, "")}`}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Emergency
            </a>
            <Link
              to="/appointment"
              className="rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 active:bg-primary-800"
            >
              Book Appointment
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay + drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-white shadow-2xl"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <Link
                  to="/"
                  className="flex items-center"
                  onClick={() => setMobileOpen(false)}
                >
                  <img src={shifaLogo} alt="Shifa Medical Centre" className="h-10 w-auto" />
                </Link>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Drawer links */}
              <nav className="flex-1 overflow-y-auto px-5 py-4">
                <ul className="space-y-1">
                  {navLinks.map((link) =>
                    link.hasDropdown ? (
                      <li key={link.label}>
                        <button
                          onClick={() => setMobileDeptOpen((p) => !p)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-lg px-3 py-3 text-base font-medium transition-colors",
                            isActive(link.href)
                              ? "bg-primary-50 text-primary-700"
                              : "text-gray-700 hover:bg-gray-50",
                          )}
                        >
                          {link.label}
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              mobileDeptOpen && "rotate-180",
                            )}
                          />
                        </button>
                        <AnimatePresence>
                          {mobileDeptOpen && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <li>
                                <Link
                                  to="/departments"
                                  className="block rounded-lg px-6 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                                >
                                  All Departments &rarr;
                                </Link>
                              </li>
                              {departments.map((dept) => (
                                <li key={dept.id}>
                                  <Link
                                    to={`/departments/${dept.id}`}
                                    className="block rounded-lg px-6 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
                                  >
                                    {dept.name}
                                  </Link>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </li>
                    ) : (
                      <li key={link.label}>
                        <Link
                          to={link.href}
                          className={cn(
                            "block rounded-lg px-3 py-3 text-base font-medium transition-colors",
                            isActive(link.href)
                              ? "bg-primary-50 text-primary-700"
                              : "text-gray-700 hover:bg-gray-50",
                          )}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ),
                  )}
                </ul>
              </nav>

              {/* Drawer footer */}
              <div className="space-y-3 border-t border-gray-100 px-5 py-4">
                <Link
                  to="/appointment"
                  className="flex w-full items-center justify-center rounded-xl bg-primary-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
                >
                  Book Appointment
                </Link>
                <a
                  href={`tel:${siteConfig.contact.emergency.replace(/-/g, "")}`}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 py-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
                >
                  <Phone className="h-4 w-4" />
                  Emergency: {siteConfig.contact.emergency}
                </a>
                <div className="flex items-center gap-4 pt-2 text-xs text-gray-500">
                  <a
                    href={`tel:${siteConfig.contact.phone.replace(/-/g, "")}`}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    <Phone className="h-3 w-3" />
                    {siteConfig.contact.phone}
                  </a>
                  <a
                    href={`mailto:${siteConfig.contact.email}`}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    <Mail className="h-3 w-3" />
                    Email
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
